<template>
  <div class="mt-5">
    <div class="columns">
      <div class="column">
        <p class="title mb-1">NFT Token List</p>
        <p class="has-text-grey">Manage all NFT tokens in the system</p>
      </div>
    </div>

    <!-- Admin check -->
    <div v-if="!isAdmin" class="notification is-warning">
      <p>You do not have admin privileges to view this page.</p>
    </div>

    <div v-else>
      <!-- Loading indicator -->
      <div v-if="loading" class="has-text-centered my-6">
        <p class="is-size-5">Loading tokens...</p>
        <progress class="progress is-primary mt-3" max="100"></progress>
      </div>

      <!-- Token list table -->
      <div v-else class="box">
        <!-- Search by user account -->
        <div class="field has-addons mb-4">
          <div class="control is-expanded">
            <input 
              class="input" 
              type="text" 
              placeholder="Search by user account (0x...)"
              v-model="searchAddress"
              :class="{ 'is-danger': searchError }"
              @keyup.enter="searchByAccount"
            >
            <p class="help is-danger" v-if="searchError">{{ searchError }}</p>
          </div>
          <div class="control">
            <button 
              class="button is-dark" 
              @click="searchByAccount"
              :disabled="isSearching"
            >
              <span v-if="isSearching">
                <i class="ri-loader-4-line is-loading"></i>
                Searching...
              </span>
              <span v-else>
                <i class="ri-search-line"></i>
                Search
              </span>
            </button>
          </div>
          <div class="control" v-if="isFiltered">
            <button class="button is-light" @click="clearSearch">
              <i class="ri-close-line"></i>
              Clear
            </button>
          </div>
        </div>
        
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable">
            <thead>
              <tr>
                <th>Token ID</th>
                <th>Owner</th>
                <th>Balance</th>
                <th>Assets</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="token in currentPageTokens" :key="token.id">
                <td>
                  <span class="tag is-info">#{{ token.id }}</span>
                </td>
                <td>
                  <div class="tags has-addons">
                    <span class="tag is-dark">
                      <i class="ri-user-line"></i>
                    </span>
                    <span class="tag is-info is-family-monospace" style="font-size: 0.7rem;">{{ token.owner }}</span>
                  </div>
                </td>
                <td>
                  <span class="tag is-info">
                    <span class="has-text-weight-bold">{{ formatNFTBalance(token.balance) }}</span>
                    <span class="ml-1">CMTYv</span>
                  </span>
                </td>
                <td>
                  <span class="tag is-info">{{ token.assets.filter(a => a.active !== false && a.name !== 'CMTY Deposit').length }}</span>
                </td>
                <td>
                  <button class="button is-dark" @click="viewTokenDetails(token.id)">
                    View Details
                  </button>
                </td>
              </tr>
              <tr v-if="currentPageTokens.length === 0">
                <td colspan="5" class="has-text-centered">No tokens found in this range</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination controls -->
        <nav class="pagination is-centered mt-4" role="navigation" aria-label="pagination">
          <button 
            class="button is-dark pagination-previous" 
            :disabled="currentPage === 1" 
            @click="prevPage"
          >
            Previous
          </button>
          <button 
            class="button is-dark pagination-next" 
            :disabled="currentPage === totalPages || totalPages === 0" 
            @click="nextPage"
          >
            Next
          </button>
          <ul class="pagination-list">
            <li v-for="page in paginationRange" :key="page">
              <a 
                class="pagination-link" 
                :class="{'is-current': page === currentPage}"
                @click="goToPage(page)"
              >
                {{ page }}
              </a>
            </li>
          </ul>
        </nav>

        <div class="has-text-centered mt-4">
          <p class="is-size-7">Showing tokens {{ startIndex + 1 }}-{{ endIndex }} of {{ totalTokens }}</p>
        </div>
      </div>
    </div>

    <!-- Token Details Modal -->
    <div class="modal" :class="{'is-active': showTokenModal}">
      <div class="modal-background" @click="closeTokenModal"></div>
      <div class="modal-card" style="width: 900px; max-width: 95vw">
        <header class="modal-card-head">
          <p class="modal-card-title">Token #{{ selectedTokenId }} Details</p>
          <button class="delete is-dark" aria-label="close" @click="closeTokenModal"></button>
        </header>
        <section class="modal-card-body" v-if="selectedToken">
          <div class="columns is-multiline">
            <!-- Token Info -->
            <div class="column is-4">
              <h4 class="title is-5 mb-4">Token Information</h4>
              
              <div class="field mb-4">
                <div class="tags has-addons">
                  <span class="tag is-dark">Token ID</span>
                  <span class="tag is-info">#{{ selectedToken.id }}</span>
                </div>
              </div>
              
              <div class="field mb-4">
                <div class="tags has-addons">
                  <span class="tag is-dark">Owner</span>
                  <span class="tag is-info is-family-monospace">{{ formatAddress(selectedToken.owner) }}</span>
                </div>
              </div>
              
              <div class="field mb-4">
                <div class="tags has-addons">
                  <span class="tag is-dark">Balance</span>
                  <span class="tag is-info">
                    <span class="has-text-weight-bold">{{ formatNFTBalance(selectedToken.balance) }}</span>
                    <span class="ml-1">CMTYv</span>
                  </span>
                </div>
              </div>
              
              <!-- Burn Token Section (Only visible for admins) -->
              <div v-if="isAdmin" class="box has-background-light mt-4">
                <h5 class="subtitle is-6 mb-3">Burn Tokens</h5>
                
                <div class="field">
                  <label class="label is-small">Amount to Burn</label>
                  <div class="control">
                    <input 
                      class="input" 
                      type="number" 
                      placeholder="Enter amount to burn" 
                      v-model="burnAmount"
                      :class="{ 'is-danger': burnAmountError }"
                    >
                  </div>
                  <p class="help">Maximum: {{ formatNFTBalance(selectedToken.balance) }}</p>
                  <p class="help is-danger" v-if="burnAmountError">{{ burnAmountError }}</p>
                </div>
                
                <div class="field">
                  <div class="control">
                    <button 
                      class="button is-dark mb-3"
                      @click="burnTokens"
                      :disabled="!canBurnTokens || isBurning"
                    >
                      <span v-if="isBurning">
                        <i class="ri-loader-4-line is-loading"></i>
                        Processing...
                      </span>
                      <span v-else>
                        <i class="ri-delete-bin-line"></i>
                        Burn Tokens
                      </span>
                    </button>
                  </div>

                  <div class="field" v-if="isAdmin">
                    <button
                      class="button is-danger"
                      @click="confirmLiquidation"
                    >
                      <i class="ri-close-circle-line mr-2"></i> Liquidate NFT
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Tabs Section -->
            <div class="column is-8">
              <!-- Tabs navigation -->
              <div class="tabs is-centered">
                <ul>
                  <li :class="{'is-active': activeTab === 'assets'}">
                    <a @click="activeTab = 'assets'">
                      <span class="icon"><i class="ri-file-list-line"></i></span>
                      <span>Assets</span>
                    </a>
                  </li>
                  <li :class="{'is-active': activeTab === 'deposits'}">
                    <a @click="activeTab = 'deposits'">
                      <span class="icon"><i class="ri-exchange-line"></i></span>
                      <span>Deposits</span>
                    </a>
                  </li>
                </ul>
              </div>

              <!-- Assets Tab Content -->
              <div v-show="activeTab === 'assets'">
                <h4 class="title is-5 mb-4">Assets ({{ selectedToken.assets.filter(a => a.active !== false).length }})</h4>
                
                <div v-if="selectedToken.assets.filter(a => a.active !== false).length > 0" class="box p-3">
                  <table class="table is-fullwidth">
                    <thead>
                      <tr>
                        <th>Asset Name</th>
                        <th>Class</th>
                        <th class="has-text-right">Value</th>
                        <th v-if="isAdmin">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(asset, index) in selectedToken.assets.filter(a => a.active !== false)" :key="index">
                        <td>
                          <div class="is-flex is-align-items-center">
                            <span class="icon mr-2">
                              <i class="ri-file-list-line"></i>
                            </span>
                            <span class="has-text-weight-bold">{{ asset.name }}</span>
                          </div>
                        </td>
                        <td>
                          <span class="tag is-info">{{ asset.assetClass }}</span>
                        </td>
                        <td class="has-text-right">
                          <span class="tag is-info">
                            <span class="has-text-weight-bold">{{ formatNFTBalance(asset.cmtyValue) }}</span>
                            <span class="ml-1">CMTYv</span>
                          </span>
                        </td>
                        <td v-if="isAdmin">
                          <button v-if="asset.name !== 'CMTY Deposit'" @click="editAsset(index)" class="button is-small is-info">
                            <span class="icon">
                              <i class="ri-edit-line"></i>
                            </span>
                            <span>Edit</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="has-background-light">
                        <td colspan="2" class="has-text-weight-bold">Total Value</td>
                        <td class="has-text-right">
                          <span class="tag is-info">
                            <span class="has-text-weight-bold">{{ calculateTotalAssetValue() }}</span>
                            <span class="ml-1">CMTYv</span>
                          </span>
                        </td>
                        <td v-if="isAdmin"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                <div v-else class="notification is-light">
                  <p>No assets found for this token</p>
                </div>
              </div>

              <!-- Deposits Tab Content -->
              <div v-show="activeTab === 'deposits'">
                <h4 class="title is-5 mb-4">Deposit History</h4>
                
                <div class="box p-3">
                  <table class="table is-fullwidth is-striped">
                    <thead>
                      <tr>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(deposit, index) in userDepositList" :key="index">
                        <td>
                          <div class="tags has-addons">
                            <span class="tag is-dark">CMTY</span>
                            <span class="tag is-info">{{ deposit.amount }}</span>
                          </div>
                        </td>
                        <td>
                          <span class="tag is-info">{{ deposit.finished }}</span>
                        </td>
                      </tr>
                      <tr v-if="userDepositList.length === 0">
                        <td colspan="2" class="has-text-centered">There are no transactions yet</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-light" @click="closeTokenModal">Close</button>
        </footer>
        
        <!-- Asset Edit Form Modal -->
        <div class="modal" :class="{'is-active': showEditForm}">
          <div class="modal-background" @click="showEditForm = false"></div>
          <div class="modal-card" style="width: 900px; max-width: 95vw">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Asset</p>
              <button class="delete is-dark" aria-label="close" @click="showEditForm = false"></button>
            </header>
            <section class="modal-card-body">
              <div class="columns">
                <div class="column is-6">
                  <!-- Asset selection -->
                  <div class="field">
                    <label class="label">Select Asset</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select v-model="selectedAssetIndex">
                          <option v-for="(asset, index) in selectedToken ? selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false) : []" :key="index" :value="index">
                            {{ asset.name }} ({{ formatNFTBalance(asset.cmtyValue) }} CMTYv)
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Remove Asset Button -->
                  <div v-if="selectedAssetIndex !== null" class="field mt-3">
                    <div class="control">
                      <button 
                        class="button is-secondary" 
                        @click="confirmDeactivateAsset"
                        type="button"
                      >
                        <span class="icon">
                          <i class="ri-delete-bin-line"></i>
                        </span>
                        <span>Remove Asset</span>
                      </button>
                    </div>
                    <p class="help">This will permanently deactivate the asset.</p>
                  </div>
                  
                  <div v-if="selectedAssetIndex !== null" class="mt-5">
                    <div class="field">
                      <label class="label">Asset Class</label>
                      <div class="control">
                        <div class="tags has-addons">
                          <span class="tag is-dark">Class</span>
                          <span class="tag is-info">{{ selectedToken && selectedAssetIndex !== null ? selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false)[selectedAssetIndex].assetClass : '' }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Value Update Form -->
                    <div class="box">
                      <h4 class="title is-6 mb-3">Update Asset Value</h4>
                      <div class="field">
                        <div class="field has-addons mb-1">
                          <p class="control is-expanded">
                            <input class="input" type="number" v-model="newAssetValue" placeholder="Enter new value" />
                          </p>
                          <p class="control">
                            <a class="button is-static"> CMTYv </a>
                          </p>
                        </div>
                      </div>
                      <div class="field">
                        <div class="control">
                          <button 
                            class="button is-dark" 
                            @click="updateAssetValue"
                            :disabled="isUpdatingValue || !newAssetValue || newAssetValue <= 0"
                            :class="{'is-loading': isUpdatingValue}"
                          >
                            Update Value
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-if="selectedAssetIndex !== null" class="column is-6">
                  <!-- Metadata Update Form -->
                  <div class="box">
                    <h4 class="title is-6 mb-3">Update Asset Metadata</h4>
                    <div class="field">
                      <label class="label">Name</label>
                      <div class="control">
                        <input 
                          class="input" 
                          type="text" 
                          v-model="editingAsset.name" 
                          placeholder="Asset name"
                        >
                      </div>
                    </div>
                    <div class="field">
                      <label class="label">Description</label>
                      <div class="control mt-2">
                        <p class="mb-1 has-text-grey is-size-7">Description</p>
                        <textarea class="textarea bg-color-main" v-model="editingAsset.description" rows="2"></textarea>
                      </div>
                    </div>
                    
                    <!-- Image Upload Section -->
                    <div class="mt-4">
                      <p class="has-text-grey is-size-7">Images</p>
                      <div class="file has-name is-fullwidth">
                        <label class="file-label">
                          <input class="file-input" type="file" @change="handleImageSelect" accept=".gif,.jpg,.png,.jpeg" multiple>
                          <span class="file-cta">
                            <span class="file-icon">
                              <i class="ri-image-add-line"></i>
                            </span>
                            <span class="file-label">
                              Choose files...
                            </span>
                          </span>
                        </label>
                      </div>

                      <!-- Image Preview Section -->
                      <div v-if="editingAsset.imageURIs && editingAsset.imageURIs.length > 0" class="mt-4">
                        <div class="columns is-multiline">
                          <div v-for="(imageUri, index) in editingAsset.imageURIs" :key="index" class="column is-3">
                            <div class="card">
                              <div class="card-image">
                                <figure class="image is-square">
                                  <img :src="imageUri" :alt="`Image ${index + 1}`" style="object-fit: cover;">
                                </figure>
                              </div>
                              <div class="card-content has-text-centered p-2">
                                <button @click="removeImage(index)" class="button is-small is-danger is-light">
                                  <span class="icon">
                                    <i class="ri-delete-bin-line"></i>
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Asset Attributes Section -->
                    <div class="field mt-5">
                      <label class="label">Attributes</label>
                      
                      <div v-if="assetAttributes.length === 0" class="notification is-light">
                        <p>No attributes found for this asset</p>
                      </div>
                      
                      <div v-for="(attribute, index) in assetAttributes" :key="index" class="field is-horizontal mb-2">
                        <div class="field-body">
                          <div class="field">
                            <div class="control">
                              <input 
                                class="input" 
                                type="text" 
                                v-model="attribute.key" 
                                placeholder="Attribute name"
                              >
                            </div>
                          </div>
                          <div class="field">
                            <div class="control">
                              <input 
                                class="input" 
                                type="text" 
                                v-model="attribute.value" 
                                placeholder="Attribute value"
                              >
                            </div>
                          </div>
                          <div class="field is-narrow">
                            <div class="control">
                              <button 
                                class="button is-secondary" 
                                @click="removeAttribute(index)"
                                type="button"
                              >
                                <i class="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div class="field mt-3">
                        <div class="control">
                          <button 
                            class="button is-outlined" 
                            @click="addAttribute"
                            type="button"
                          >
                            Add Attribute
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="field mt-5">
                      <div class="control">
                        <button 
                          class="button is-dark" 
                          @click="updateAssetMetadata"
                          :disabled="isUpdatingMetadata || !editingAsset.name"
                          :class="{'is-loading': isUpdatingMetadata}"
                        >
                          Update Metadata
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <footer class="modal-card-foot">
              <button class="button is-light" @click="showEditForm = false">Close</button>
            </footer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ADMIN_ROLE, MINTER_ROLE } from '@/constants/roles';
import * as util from "@/utilities/util.js";
import * as fileService from "@/services/file_service.js";
import Swal from 'sweetalert2';

export default {
  name: "TokenListPage",
  data() {
    return {
      currentImageIndex: 0,
      isConnected: false,
      account: null,
      isAdmin: false,
      isMinterUser: false,
      loading: true,
      nftContractInstance: null,
      orchestratorContract: null,
      oracleContract: null,
      
      // Token data
      tokens: [],
      totalTokens: 0,
      
      // Pagination
      currentPage: 1,
      pageSize: 10,
      
      // Modal
      showTokenModal: false,
      selectedTokenId: null,
      selectedToken: null,
      activeTab: 'assets',
      userDepositList: [],
      
      // Asset editing
      showEditForm: false,
      selectedAssetIndex: null,
      editingAsset: null,
      newAssetValue: '',
      newAssetName: "",
      newAssetDescription: "",
      newAssetImageURI: "",
      attributeKeys: [],
      attributeValues: [],
      assetAttributes: [],
      isUpdatingValue: false,
      isUpdatingMetadata: false,
      isDeactivatingAsset: false,
      // Propiedades para la funcionalidad de quemado de tokens
      burnAmount: "",
      burnAmountError: "",
      isBurning: false,
      
      // Propiedades para la búsqueda por cuenta de usuario
      searchAddress: "",
      searchError: "",
      isSearching: false,
      isFiltered: false,
      filteredTokens: [],
    };
  },
  watch: {
    selectedAssetIndex: {
      immediate: true,
      handler() {
        this.watchSelectedAssetIndex();
      }
    }
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.totalTokens);
    },
    totalPages() {
      return Math.ceil(this.totalTokens / this.pageSize);
    },
    currentPageTokens() {
      // Si estamos filtrando por cuenta de usuario, mostramos los tokens filtrados
      if (this.isFiltered) {
        return this.filteredTokens;
      }
      // De lo contrario, mostramos los tokens ya cargados para la página actual
      // this.tokens ya contiene solo los tokens de la página actual después de loadTokensForCurrentPage()
      return this.tokens;
    },
    paginationRange() {
      const range = [];
      const maxVisiblePages = 5;
      
      if (this.totalPages <= maxVisiblePages) {
        // Show all pages if there are few
        for (let i = 1; i <= this.totalPages; i++) {
          range.push(i);
        }
      } else {
        // Always show first page
        range.push(1);
        
        // Calculate middle range
        let startPage = Math.max(2, this.currentPage - 1);
        let endPage = Math.min(this.totalPages - 1, this.currentPage + 1);
        
        // Adjust if at the beginning or end
        if (this.currentPage <= 2) {
          endPage = 4;
        } else if (this.currentPage >= this.totalPages - 1) {
          startPage = this.totalPages - 3;
        }
        
        // Add ellipsis if needed
        if (startPage > 2) {
          range.push('...');
        }
        
        // Add middle pages
        for (let i = startPage; i <= endPage; i++) {
          range.push(i);
        }
        
        // Add ellipsis if needed
        if (endPage < this.totalPages - 1) {
          range.push('...');
        }
        
        // Always show last page
        range.push(this.totalPages);
      }
      
      return range;
    },
    /**
     * @notice Checks if the current burn amount is valid and can be processed
     * @returns {boolean} True if the burn amount is valid and can be processed
     */
    canBurnTokens() {
      // Basic validation
      if (!this.selectedToken) {
        return false;
      }
      
      if (!this.burnAmount) {
        return false;
      }
      
      if (this.burnAmountError) {
        return false;
      }
      
      try {
        // Parse values for comparison
        const burnAmount = parseFloat(this.burnAmount);
        const balance = parseFloat(this.selectedToken.balance);
        
        // Check if burn amount is valid
        if (isNaN(burnAmount) || burnAmount <= 0) {
          return false;
        }
        
        // Check if burn amount is within balance
        if (isNaN(balance) || burnAmount > balance) {
          return false;
        }
        
        return true;
      } catch (error) {
        console.error('Error in canBurnTokens calculation:', error);
        return false;
      }
    }
  },
  async mounted() {
    this.$watch(
      () => {
        return this.$store.state.isConnected;
      },
      (val) => {
        this.isConnected = val;
      }
    );

    await util.reconnect();
    this.$store.commit("changeConnectionStatus", true);
    this.account = await util.getCoinbase();
    this.$store.commit("changeAccount", this.account);

    this.nftContractInstance = await util.NFTContractInstance();
    this.orchestratorContract = await util.OrchestratorContractInstance();
    this.oracleContract = await util.OracleContractInstance();
    
    await this.checkAdminRole();
    await this.checkMinterRole();
    if (this.isAdmin) {
      await this.loadTokens();
    } else {
      this.loading = false;
    }
  },
  methods: {
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },
    
    nextImage() {
      if (this.editingAsset && 
          this.editingAsset.imageURIs && 
          this.currentImageIndex < this.editingAsset.imageURIs.length - 1) {
        this.currentImageIndex++;
      }
    },

    formatAddress(address) {
      if (!address) return '';
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    },
    
    formatNFTBalance(balance) {
      // Si el balance es 0 o null, retornar 0
      if (!balance) return '0';
      
      try {
        // Usar util.subtractDecimals para obtener el valor en formato decimal
        const decimalValue = util.subtractDecimals(balance, 18);
        
        // Convertir a número y redondear
        const roundedValue = Math.round(parseFloat(decimalValue));
        
        // Formatear con separadores de miles
        return new Intl.NumberFormat('en-US').format(roundedValue);
      } catch (error) {
        console.error('Error formatting balance:', error);
        return '0';
      }
    },
    
    /**
     * @notice Calculates the total value of all active assets in the token
     * @returns {string} The total value formatted with separators
     */
    calculateTotalAssetValue() {
      if (!this.selectedToken || !this.selectedToken.assets) return '0';
      
      // Filter active assets
      const activeAssets = this.selectedToken.assets.filter(asset => asset.active !== false);
      
      // Calculate total by summing all cmtyValue values
      let totalValue = 0n; // Use BigInt for handling large numbers
      
      for (const asset of activeAssets) {
        if (asset.cmtyValue) {
          // Convert to BigInt to handle large numbers properly
          totalValue += BigInt(asset.cmtyValue);
        }
      }
      
      // Format the total with the same formatting as individual values
      return this.formatNFTBalance(totalValue.toString());
    },
    
    async checkAdminRole() {
      if (!this.account) return false;
      try {
        const ADMIN_ROLE = await this.nftContractInstance.methods.ADMIN_ROLE().call();
        const hasRole = await this.nftContractInstance.methods.hasRole(ADMIN_ROLE, this.account).call();
        this.isAdmin = hasRole;
        return hasRole;
      } catch (error) {
        console.error('Error checking admin role:', error);
        return false;
      }
    },
    
    async checkMinterRole() {
      if (!this.account) return false;
      try {
        const MINTER_ROLE = await this.orchestratorContract.methods.MINTER_ROLE().call();
        const hasRole = await this.orchestratorContract.methods.hasRole(MINTER_ROLE, this.account).call();
        this.isMinterUser = hasRole;
        return hasRole;
      } catch (error) {
        console.error('Error checking minter role:', error);
        return false;
      }
    },
    
    async loadTokens() {
      this.loading = true;
      this.tokens = [];
      
      try {
        // Get the last token ID
        const lastTokenId = await this.nftContractInstance.methods.lastTokenId().call();
        this.totalTokens = parseInt(lastTokenId);
        console.log('Total tokens:', this.totalTokens);
        
        // Load tokens for the current page
        await this.loadTokensForCurrentPage();
      } catch (error) {
        console.error('Error loading tokens:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadTokensForCurrentPage() {
      // Si estamos filtrando, no recargamos los tokens
      if (this.isFiltered) {
        return;
      }
      
      // Clear current tokens
      this.tokens = [];
      
      // Calculate range for current page
      const start = this.startIndex;
      const end = this.endIndex;
      
      // Load tokens in the range
      for (let i = start + 1; i <= end && i <= this.totalTokens; i++) {
        await this.loadTokenInfo(i);
      }
    },
    
    /**
     * @notice Busca un token por la cuenta de usuario
     * @dev Valida la dirección y busca el token asociado a esa cuenta
     */
    async searchByAccount() {
      // Resetear errores y estado de búsqueda
      this.searchError = "";
      this.isSearching = true;
      this.filteredTokens = [];
      
      try {
        // Validar que la dirección tenga el formato correcto
        if (!this.searchAddress || !this.isValidAddress(this.searchAddress)) {
          this.searchError = "Please enter a valid Ethereum address (0x...)";
          return;
        }
        
        // Normalizar la dirección (convertir a minúsculas)
        const normalizedAddress = this.searchAddress.toLowerCase();
        
        // Verificar si el usuario tiene un token asignado
        const userTokenId = await this.nftContractInstance.methods.getUserToken(normalizedAddress).call();
        
        if (userTokenId === "0") {
          this.searchError = "This account does not have any tokens";
          this.isFiltered = true;
          return;
        }
        
        // Limpiar los tokens actuales para evitar duplicados
        this.tokens = [];
        
        // Cargar la información del token directamente en filteredTokens
        try {
          // Verificar si el token existe
          if (parseInt(userTokenId) <= 0) {
            this.searchError = "Invalid token ID";
            return;
          }
          
          // Obtener el propietario del token para confirmar
          const owner = await this.nftContractInstance.methods.ownerOf(userTokenId).call();
          
          if (!owner || owner === '0x0000000000000000000000000000000000000000') {
            this.searchError = "Token has no owner";
            return;
          }
          
          // Verificar que la dirección coincida con el propietario
          if (owner.toLowerCase() !== normalizedAddress) {
            this.searchError = "Address does not match token owner";
            return;
          }
          
          // Get token balance
          const balance = await this.nftContractInstance.methods.balanceOf(owner, userTokenId).call();
          
          // Get token assets
          let assets = [];
          let assetsCount = 0;
          
          try {
            assetsCount = await this.nftContractInstance.methods.getTokenAssetsLength(userTokenId).call();
            if (parseInt(assetsCount) > 0) {
              assets = await this.nftContractInstance.methods.getTokenAssets(userTokenId).call();
            }
          } catch (assetsError) {
            console.log(`Error getting assets for token ${userTokenId}:`, assetsError);
          }
          
          // Process assets to ensure active property is correctly handled
          let processedAssets = [];
          if (Array.isArray(assets)) {
            processedAssets = assets.map(asset => {
              // Make sure active property is a boolean
              return {
                ...asset,
                active: asset.active === undefined ? true : Boolean(asset.active)
              };
            });
          }
          
          // Add token directly to filteredTokens
          this.filteredTokens = [{
            id: parseInt(userTokenId),
            owner,
            balance,
            assets: processedAssets,
            assetsCount: parseInt(assetsCount)
          }];
          
          // Marcar que estamos filtrando
          this.isFiltered = true;
        } catch (error) {
          console.error(`Error loading token ${userTokenId}:`, error);
          this.searchError = "Error loading token details";
        }
      } catch (error) {
        console.error('Error searching by account:', error);
        this.searchError = "Error searching for token: " + error.message;
      } finally {
        this.isSearching = false;
      }
    },
    
    /**
     * @notice Limpia la búsqueda y muestra todos los tokens
     */
    clearSearch() {
      this.searchAddress = "";
      this.searchError = "";
      this.isFiltered = false;
      this.filteredTokens = [];
      this.loadTokensForCurrentPage();
    },
    
    /**
     * @notice Valida si una dirección tiene el formato correcto
     * @param {string} address - La dirección a validar
     * @returns {boolean} - True si la dirección es válida
     */
    isValidAddress(address) {
      return /^0x[a-fA-F0-9]{40}$/.test(address);
    },
    
    async loadTokenInfo(tokenId) {
      try {
        // Verificar si el token existe
        if (tokenId <= 0) return;
        
        // Intentar obtener el propietario del token
        let owner = null;
        try {
          owner = await this.nftContractInstance.methods.ownerOf(tokenId).call();
        } catch (ownerError) {
          // Si el token no existe o no tiene propietario, lo omitimos
          return;
        }
        
        if (!owner || owner === '0x0000000000000000000000000000000000000000') {
          return;
        }
        
        // Verificar que este token sea realmente el asignado al usuario
        // usando getUserToken (que es el método correcto según el diseño del contrato)
        const userToken = await this.nftContractInstance.methods.getUserToken(owner).call();
        
        // Si el token que estamos revisando no coincide con el token asignado al usuario, lo omitimos
        if (parseInt(userToken) !== parseInt(tokenId)) {
          console.log(`Token ${tokenId} no es el token asignado al usuario ${owner} (su token es ${userToken})`);
          return;
        }
        
        // Get token balance
        const balance = await this.nftContractInstance.methods.balanceOf(owner, tokenId).call();
        
        // Get token assets
        let assets = [];
        let assetsCount = 0;
        
        try {
          assetsCount = await this.nftContractInstance.methods.getTokenAssetsLength(tokenId).call();
          if (parseInt(assetsCount) > 0) {
            assets = await this.nftContractInstance.methods.getTokenAssets(tokenId).call();
          }
        } catch (assetsError) {
          console.log(`Error getting assets for token ${tokenId}:`, assetsError);
        }
        
        // Check if we already have a token with this owner
        const existingTokenWithSameOwner = this.tokens.find(t => t.owner === owner);
        
        if (existingTokenWithSameOwner) {
          console.log(`Warning: Found multiple tokens (${existingTokenWithSameOwner.id} and ${tokenId}) with the same owner ${owner}. This should not happen according to contract design.`);
          // In this case, we'll keep both for debugging purposes, but highlight the issue
        }
        
        // Process assets to ensure active property is correctly handled
        let processedAssets = [];
        if (Array.isArray(assets)) {
          processedAssets = assets.map(asset => {
            // Make sure active property is a boolean
            return {
              ...asset,
              active: asset.active === undefined ? true : Boolean(asset.active)
            };
          });
        }
        
        // Add token to list
        this.tokens.push({
          id: tokenId,
          owner,
          balance,
          assets: processedAssets,
          assetsCount: parseInt(assetsCount)
        });
        
        // Sort tokens by ID
        this.tokens.sort((a, b) => a.id - b.id);
      } catch (error) {
        console.error(`Error loading token ${tokenId}:`, error);
      }
    },
    
    async viewTokenDetails(tokenId) {
      this.selectedTokenId = tokenId;
      
      try {
        // Get fresh token data from the contract
        const tokenData = await this.nftContractInstance.methods.getTokenAssets(tokenId).call();
        console.log('Token assets:', tokenData);
        
        // Get owner info
        const owner = await this.nftContractInstance.methods.ownerOf(tokenId).call();
        const balance = await this.nftContractInstance.methods.balanceOf(owner, tokenId).call();
        
        // Create token object
        const token = {
          id: tokenId,
          owner: owner,
          balance: balance,
          assets: tokenData.map(asset => ({
            ...asset,
            imageURIs: Array.isArray(asset.imageURIs) ? asset.imageURIs : 
                      (asset.imageURI ? [asset.imageURI] : [])
          }))
        };
        
        this.selectedToken = token;
        this.showTokenModal = true;
        
        // Load deposits for the token owner
        await this.getDepositsForUser(owner);
        
        // Reset edit form state
        this.showEditForm = false;
        this.selectedAssetIndex = null;
        this.editingAsset = {
          name: '',
          description: '',
          assetClass: '',
          imageURIs: [],
          cmtyValue: '0'
        };
        this.newAssetValue = '';
        this.currentImageIndex = 0;
      } catch (error) {
        console.error('Error loading token details:', error);
        util.toast.fire({ icon: "error", title: "Error loading token details" });
      }
    },
    
    closeTokenModal() {
      this.showTokenModal = false;
      this.selectedTokenId = null;
      this.selectedToken = null;
      this.selectedAssetIndex = null;
      this.showEditForm = false;
      this.activeTab = 'assets';
      this.userDepositList = [];
    },
    
    async getDepositsForUser(ownerAddress) {
      this.userDepositList = [];

      try {
        const txCount = await this.oracleContract.methods
          .txsCount(ownerAddress)
          .call();

        for (let i = 0; i < txCount; i++) {
          const tx = await this.oracleContract.methods
            .txsPerUser(ownerAddress, i)
            .call();
          if (tx.toCyclos) {
            this.userDepositList.push({
              amount: util.subtractDecimals(tx.amount, 18),
              finished: tx.finished ? "Processed" : "Pending",
            });
          }
        }
      } catch (error) {
        console.error('Error loading deposits:', error);
      }
    },
    
    /**
     * @notice Opens the edit form for a specific asset
     * @param {Number} index The index of the asset in the filtered array
     */
    editAsset(index) {
      if (!this.isAdmin || !this.selectedToken) return;
      
      // Get the filtered assets array
      const filteredAssets = this.selectedToken.assets.filter(a => a.active !== false && a.name !== 'CMTY Deposit');
      const asset = filteredAssets[index];
      
      if (!asset) {
        util.toast.fire({ icon: "error", title: "Asset not found" });
        return;
      }
      
      // Set the selected asset index
      this.selectedAssetIndex = index;
      
      // Initialize editing asset with all required properties
      this.editingAsset = {
        name: asset.name || '',
        description: asset.description || '',
        assetClass: asset.assetClass || '',
        imageURIs: Array.isArray(asset.imageURIs) ? [...asset.imageURIs] : 
                  (asset.imageURI ? [asset.imageURI] : []),
        cmtyValue: asset.cmtyValue || '0'
      };
      
      console.log('Editing asset:', this.editingAsset);
      
      // Reset image index
      this.currentImageIndex = 0;
      
      // Show the edit form modal
      this.showEditForm = true;
    },
    
    // Watch for changes in selectedAssetIndex to update the editing asset
    watchSelectedAssetIndex() {
      console.log('watchSelectedAssetIndex called - selectedAssetIndex:', this.selectedAssetIndex);
      console.log('selectedToken:', this.selectedToken);
      
      if (this.selectedAssetIndex !== null && this.selectedToken && this.selectedToken.assets) {
        // Find the actual asset in the array by filtering out CMTY Deposit assets and inactive assets
        const filteredAssets = this.selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false);
        
        // The selectedAssetIndex is relative to the filtered array in the dropdown
        if (filteredAssets.length > this.selectedAssetIndex) {
          const currentAsset = filteredAssets[this.selectedAssetIndex];
          console.log('Found asset to edit:', currentAsset);
          
          // Create a deep copy of the asset to avoid modifying the original directly
          this.editingAsset = {
            name: currentAsset.name || '',
            description: currentAsset.description || '',
            imageURIs: Array.isArray(currentAsset.imageURIs) ? [...currentAsset.imageURIs] : 
                      (currentAsset.imageURI ? [currentAsset.imageURI] : []),
            assetClass: currentAsset.assetClass || '',
            cmtyValue: currentAsset.cmtyValue || '0',
            active: currentAsset.active !== undefined ? currentAsset.active : true
          };
          
          // Initialize the new value field with the current value (converted from wei)
          try {
            const valueInEther = window.myWeb3.utils.fromWei(currentAsset.cmtyValue.toString(), 'ether');
            this.newAssetValue = valueInEther;
          } catch (error) {
            console.error('Error converting value to ether:', error);
            this.newAssetValue = '0';
          }
          
          // Log the current values for debugging
          console.log('Editing asset:', this.editingAsset);
          console.log('Current CMTYv value:', this.newAssetValue);
          
          // Load asset attributes
          this.loadAssetAttributes();
          return;
        }
      }
      
      // If we reach here, either no asset is selected or there was an error finding it
      console.log('No valid asset found or no asset selected');
      this.editingAsset = {
        name: '',
        description: '',
        imageURIs: [],
        assetClass: '',
        cmtyValue: '0',
        active: true
      };
      this.newAssetValue = '';
      this.assetAttributes = [];
    },
    
    async updateAssetValue() {
      if (!this.isAdmin) {
        util.toast.fire({ icon: "error", title: "Only admins can update asset values" });
        return;
      }
      
      if (!this.selectedToken || this.selectedAssetIndex === null) {
        util.toast.fire({ icon: "warning", title: "Please select an asset to update" });
        return;
      }
      
      // Store the new value locally to prevent it from being reset
      const newValue = this.newAssetValue;
      
      if (!newValue || newValue <= 0) {
        util.toast.fire({ icon: "warning", title: "Please enter a valid value greater than 0" });
        return;
      }
      
      this.isUpdatingValue = true;
      
      try {
        // Get the filtered assets (excluding CMTY Deposit and inactive assets)
        const filteredAssets = this.selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false);
        
        // Find the actual index in the original assets array
        let actualAssetIndex = -1;
        for (let i = 0, filteredIdx = 0; i < this.selectedToken.assets.length; i++) {
          if (this.selectedToken.assets[i].name !== 'CMTY Deposit' && this.selectedToken.assets[i].active !== false) {
            if (filteredIdx === this.selectedAssetIndex) {
              actualAssetIndex = i;
              break;
            }
            filteredIdx++;
          }
        }
        
        if (actualAssetIndex === -1) {
          util.toast.fire({ icon: "error", title: "Could not find the selected asset" });
          return;
        }
        
        // Convert to wei (18 decimals)
        const valueInWei = window.myWeb3.utils.toWei(newValue.toString(), 'ether');
        
        // Call the contract method to update the asset value
        await this.nftContractInstance.methods.updateAssetValue(
          this.selectedToken.id,
          actualAssetIndex,
          valueInWei
        ).send({ from: this.account });
        
        // Reset the input field after successful update
        this.newAssetValue = null;
        
        // Instead of modifying the original object, reload the token data
        await this.loadTokenInfo(this.selectedToken.id);
        
        // Find the token again in the updated list
        const updatedToken = this.tokens.find(t => t.id == this.selectedToken.id);
        if (updatedToken) {
          this.selectedToken = updatedToken;
        }
        
        // Show success message
        util.toast.fire({ 
          icon: "success", 
          title: "Asset value updated successfully",
          timer: 1500,
          willClose: () => {
            // Reload the page after the toast is closed
            window.location.href = '/token-list';
          }
        });
      } catch (error) {
        console.error('Error updating asset value:', error);
        util.toast.fire({ icon: "error", title: "Error updating asset value: " + error.message });
      } finally {
        this.isUpdatingValue = false;
      }
    },
    
    async handleImageSelect(event) {
      const files = Array.from(event.target.files);
      if (!files.length) return;

      const allowedExtensions = ['.gif', '.jpg', '.png', '.jpeg'];
      
      // Validate all files first
      const invalidFiles = files.filter(file => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return !allowedExtensions.includes(fileExtension);
      });

      if (invalidFiles.length > 0) {
        util.toast.fire({ 
          icon: "error", 
          title: "Some files have invalid types. Please upload only images (gif, jpg, png, jpeg)" 
        });
        return;
      }

      let loader = this.$loading.show({});
      
      try {
        // Initialize imageURIs array if it doesn't exist
        if (!this.editingAsset.imageURIs) {
          this.editingAsset.imageURIs = [];
        }

        // Upload all files in parallel
        const uploadPromises = files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          try {
            const response = await fileService.uploadFileToIPFS(formData);
            return 'https://ipfs.io/ipfs/' + response.data.IpfsHash;
          } catch (error) {
            console.error(`Error uploading ${file.name}:`, error);
            return null;
          }
        });

        const results = await Promise.all(uploadPromises);
        
        // Filter out failed uploads and add successful ones to the array
        const successfulUploads = results.filter(uri => uri !== null);
        this.editingAsset.imageURIs.push(...successfulUploads);
        
        // Show appropriate success/warning message
        if (successfulUploads.length === files.length) {
          util.toast.fire({ 
            icon: "success", 
            title: `${successfulUploads.length} images uploaded successfully` 
          });
        } else {
          util.toast.fire({ 
            icon: "warning", 
            title: `${successfulUploads.length} of ${files.length} images uploaded successfully` 
          });
        }
      } catch (error) {
        console.error('Error in bulk upload:', error);
        util.toast.fire({ 
          icon: "error", 
          title: "Error uploading images. Please try again." 
        });
      } finally {
        loader.hide();
      }
    },

    removeImage(index) {
      if (this.editingAsset.imageURIs) {
        this.editingAsset.imageURIs.splice(index, 1);
      }
    },

    async updateAssetMetadata() {
      if (!this.isAdmin) {
        util.toast.fire({ icon: "error", title: "Only admins can update asset metadata" });
        return;
      }
      
      if (!this.selectedToken || this.selectedAssetIndex === null || !this.editingAsset) {
        util.toast.fire({ icon: "warning", title: "Please select an asset to update" });
        return;
      }
      
      if (!this.editingAsset.name) {
        util.toast.fire({ icon: "warning", title: "Asset name is required" });
        return;
      }
      
      this.isUpdatingMetadata = true;
      
      try {
        // Get the filtered assets (excluding CMTY Deposit and inactive assets)
        const filteredAssets = this.selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false);
        
        // Find the actual index in the original assets array
        let actualAssetIndex = -1;
        for (let i = 0, filteredIdx = 0; i < this.selectedToken.assets.length; i++) {
          if (this.selectedToken.assets[i].name !== 'CMTY Deposit' && this.selectedToken.assets[i].active !== false) {
            if (filteredIdx === this.selectedAssetIndex) {
              actualAssetIndex = i;
              break;
            }
            filteredIdx++;
          }
        }
        
        if (actualAssetIndex === -1) {
          util.toast.fire({ icon: "error", title: "Could not find the selected asset" });
          return;
        }
        
        // Prepare arrays for attributes from the assetAttributes array
        const attributeKeys = this.assetAttributes.map(attr => attr.key);
        const attributeValues = this.assetAttributes.map(attr => attr.value);
        
        // Use the current editing images
        const imageURIs = this.editingAsset.imageURIs || [];
        
        // Ensure we have at least one image URI
        if (!imageURIs || imageURIs.length === 0) {
          util.toast.fire({ icon: "warning", title: "At least one image is required" });
          return;
        }

        // Call the contract method to update the asset metadata
        await this.nftContractInstance.methods.updateAssetMetadata(
          this.selectedToken.id,
          actualAssetIndex,
          this.editingAsset.name,
          this.editingAsset.description || '',
          imageURIs,
          attributeKeys,
          attributeValues
        ).send({ from: this.account });
        
        // Instead of modifying the original object, reload the token data
        await this.loadTokenInfo(this.selectedToken.id);
        
        // Find the token again in the updated list
        const updatedToken = this.tokens.find(t => t.id == this.selectedToken.id);
        if (updatedToken) {
          this.selectedToken = updatedToken;
        }
        
        // Show success message
        util.toast.fire({ icon: "success", title: "Asset metadata updated successfully" });
      } catch (error) {
        console.error('Error updating asset metadata:', error);
        util.toast.fire({ icon: "error", title: "Error updating asset metadata: " + error.message });
      } finally {
        this.isUpdatingMetadata = false;
      }
    },
    
    // Load asset attributes from the contract
    async loadAssetAttributes() {
      if (!this.selectedToken || this.selectedAssetIndex === null) {
        this.assetAttributes = [];
        return;
      }
      
      try {
        // Get the filtered assets (excluding CMTY Deposit and inactive assets)
        const filteredAssets = this.selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false);
        
        // Find the actual index in the original assets array
        let actualAssetIndex = -1;
        for (let i = 0, filteredIdx = 0; i < this.selectedToken.assets.length; i++) {
          if (this.selectedToken.assets[i].name !== 'CMTY Deposit' && this.selectedToken.assets[i].active !== false) {
            if (filteredIdx === this.selectedAssetIndex) {
              actualAssetIndex = i;
              break;
            }
            filteredIdx++;
          }
        }
        
        if (actualAssetIndex === -1) {
          console.error('Could not find the selected asset');
          this.assetAttributes = [];
          return;
        }
        
        // Call the contract method to get asset attributes with the actual index
        const result = await this.nftContractInstance.methods.getAssetAttributes(
          this.selectedToken.id,
          actualAssetIndex
        ).call();
        
        const keys = result.keys || [];
        const values = result.values || [];
        
        // Reset attributes array
        this.assetAttributes = [];
        
        // Create attribute objects from the keys and values arrays
        for (let i = 0; i < keys.length; i++) {
          this.assetAttributes.push({
            key: keys[i],
            value: values[i]
          });
        }
      } catch (error) {
        console.error('Error loading asset attributes:', error);
        this.assetAttributes = [];
      }
    },
    
    // Add a new empty attribute to the list
    addAttribute() {
      this.assetAttributes.push({
        key: '',
        value: ''
      });
    },
    
    // Remove an attribute from the list
    removeAttribute(index) {
      this.assetAttributes.splice(index, 1);
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadTokensForCurrentPage();
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadTokensForCurrentPage();
      }
    },
    
    goToPage(page) {
      if (typeof page === 'number' && page !== this.currentPage) {
        this.currentPage = page;
        this.loadTokensForCurrentPage();
      }
    },
    
    /**
     * @notice Confirms with the user before deactivating an asset
     * @dev Shows a confirmation dialog using SweetAlert2
     */
    confirmDeactivateAsset() {
      if (!this.isAdmin) {
        util.toast.fire({ icon: "error", title: "Only admins can deactivate assets" });
        return;
      }
      
      if (!this.selectedToken || this.selectedAssetIndex === null) {
        util.toast.fire({ icon: "warning", title: "Please select an asset to deactivate" });
        return;
      }
      
      // Get the filtered assets (excluding CMTY Deposit and inactive assets)
      const filteredAssets = this.selectedToken.assets.filter(a => a.name !== 'CMTY Deposit' && a.active !== false);
      const assetName = filteredAssets[this.selectedAssetIndex].name;
      
      // Find the actual index in the original assets array
      let actualAssetIndex = -1;
      for (let i = 0, filteredIdx = 0; i < this.selectedToken.assets.length; i++) {
        if (this.selectedToken.assets[i].name !== 'CMTY Deposit' && this.selectedToken.assets[i].active !== false) {
          if (filteredIdx === this.selectedAssetIndex) {
            actualAssetIndex = i;
            break;
          }
          filteredIdx++;
        }
      }
      
      if (actualAssetIndex === -1) {
        util.toast.fire({ icon: "error", title: "Could not find the selected asset" });
        return;
      }
      
      // Show confirmation dialog using browser's native confirm
      const confirmed = window.confirm(`Are you sure you want to deactivate the asset '${assetName}'? This action cannot be undone.`);
      
      if (confirmed) {
        this.deactivateAsset(this.selectedToken.id, actualAssetIndex);
      }
    },
    
    /**
     * @notice Deactivates an asset in the NFT contract
     * @dev Calls the deactivateAsset method in the CMTYNFT contract
     * @param tokenId The ID of the token containing the asset
     * @param assetIndex The index of the asset to deactivate
     */
    async deactivateAsset(tokenId, assetIndex) {
      if (!this.isAdmin) {
        util.toast.fire({ icon: "error", title: "Only admins can deactivate assets" });
        return;
      }
      
      this.isDeactivatingAsset = true;
      
      try {
        // Call the contract method to deactivate the asset
        await this.nftContractInstance.methods.deactivateAsset(
          tokenId,
          assetIndex
        ).send({ from: this.account });
        
        // Immediately update the local asset data to reflect the change
        // This ensures the UI updates without needing to reload the page
        if (this.selectedToken && this.selectedToken.assets && this.selectedToken.assets[assetIndex]) {
          // Mark the asset as inactive in our local data
          this.selectedToken.assets[assetIndex] = {
            ...this.selectedToken.assets[assetIndex],
            active: false
          };
          
          // Also update the asset in the tokens array
          const tokenIndex = this.tokens.findIndex(t => t.id == tokenId);
          if (tokenIndex !== -1 && this.tokens[tokenIndex].assets && this.tokens[tokenIndex].assets[assetIndex]) {
            this.tokens[tokenIndex].assets[assetIndex] = {
              ...this.tokens[tokenIndex].assets[assetIndex],
              active: false
            };
          }
        }
        
        // Reset the selected asset index since the asset is now deactivated
        this.selectedAssetIndex = null;
        
        // Force a refresh of the dropdown by temporarily clearing and resetting the selectedToken
        const tempToken = this.selectedToken;
        this.selectedToken = null;
        
        // Use setTimeout with 0ms delay to simulate Vue's nextTick behavior
        setTimeout(() => {
          this.selectedToken = tempToken;
        }, 0);
        
        // Show success message
        util.toast.fire({ icon: "success", title: "Asset deactivated successfully" });
      } catch (error) {
        console.error('Error deactivating asset:', error);
        util.toast.fire({ icon: "error", title: "Error deactivating asset: " + error.message });
      } finally {
        this.isDeactivatingAsset = false;
      }
    },
    
    /**
     * @notice Burns tokens from the selected token
     * @dev Calls the burn method in the CMTYNFT contract
     */
    async confirmLiquidation() {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Confirm Liquidation',
        text: `Are you sure you want to liquidate the NFT of user ${this.selectedToken.owner}? This will remove all their assets and return all funds. This operation cannot be undone.`,
        showCancelButton: true,
        confirmButtonText: 'Yes, liquidate',
        confirmButtonColor: '#dc3545',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      });

      if (result.isConfirmed) {
        await this.liquidateNft();
      }
    },

    async liquidateNft() {
      try {
        const tx = await this.orchestratorContract.methods
          .liquidate(this.selectedToken.owner)
          .send({ from: this.account });
        await this.loadTokens();
        this.closeTokenModal();
      } catch (error) {
        console.error('Error liquidating NFT:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'There was an error liquidating the NFT. Please try again.'
        });
      }
    },

    async burnTokens() {
      this.burnAmountError = "";
      this.isBurning = true;
      
      try {
        // Verify connection status from store
        const isConnected = this.$store.state.isConnected;
        
        // Get current account
        const currentAccount = await util.getCoinbase();
        
        // Re-check admin role to ensure it's up to date
        await this.checkAdminRole();
        
        console.log('Connection status:', isConnected, 'Admin status:', this.isAdmin);
        
        // Check if user is connected and is admin
        if (!isConnected || !this.isAdmin) {
          util.toast.fire({ icon: "error", title: "Only admins can burn tokens" });
          this.isBurning = false;
          return;
        }
        
        // Check if token is selected
        if (!this.selectedToken) {
          util.toast.fire({ icon: "error", title: "No token selected" });
          this.isBurning = false;
          return;
        }
        
        // Validate burn amount
        try {
          const burnAmount = parseFloat(this.burnAmount);
          const balance = parseFloat(this.selectedToken.balance);
          
          if (isNaN(burnAmount) || burnAmount <= 0) {
            this.burnAmountError = "Amount must be greater than 0";
            this.isBurning = false;
            util.toast.fire({ icon: "error", title: "Amount must be greater than 0" });
            return;
          }
          
          if (isNaN(balance) || burnAmount > balance) {
            this.burnAmountError = "Amount exceeds token's balance";
            this.isBurning = false;
            util.toast.fire({ icon: "error", title: "Amount exceeds token's balance" });
            return;
          }
        } catch (error) {
          console.error('Error validating burn amount:', error);
          this.burnAmountError = "Invalid number format";
          this.isBurning = false;
          util.toast.fire({ icon: "error", title: "Invalid number format" });
          return;
        }
        
        // Get owner address from the selected token
        const ownerAddress = this.selectedToken.owner;
        
        // Convert burn amount to a proper format for blockchain with 18 decimals
        const burnAmountBN = BigInt(util.addDecimals(this.burnAmount, 18)).toString();
        
        // Call the removeTokens method from orchestrator
        await this.orchestratorContract.methods
          .removeTokens(ownerAddress, burnAmountBN)
          .send({ from: this.account });
        
        // Show success message
        util.toast.fire({ 
          icon: "success", 
          title: `Successfully burned ${this.burnAmount} tokens from token #${this.selectedToken.id}`
        });
        
        // Reset form and refresh data
        this.burnAmount = "";
        this.burnAmountError = "";
        
        // Refresh token data
        await this.viewTokenDetails(this.selectedToken.id);
      } catch (error) {
        console.error("Error burning tokens:", error);
        util.toast.fire({ icon: "error", title: "Error burning tokens: " + error.message });
      } finally {
        this.isBurning = false;
      }
    }
  }
};
</script>

<style scoped>
.has-border-bottom {
  border-bottom: 1px solid #f0f0f0;
}

.thumbnail-selected {
  border-color: #3273dc !important;
}

.thumbnail-container:hover {
  border-color: #dbdbdb;
}
</style>
